import type { ReactNode } from 'react'

import type { StatusEntry } from '../data/public-status'
import { labelFor, toneFor } from '../data/state'

import { StateMarker } from './state-marker'

/*
 * A plate of component or region rows. `DESIGN_SYSTEM.md` §4.1: adjacent panels
 * share a border, so this is one bordered region with divided rows — never a
 * stack of cards with gaps. Radius 0, no shadow.
 *
 * ── Every row is present, whatever its state ───────────────────────────────
 *
 * Including the ones nobody is measuring. Filtering out an `unknown` component
 * would leave a page of green rows that looks complete and is not, which is the
 * §5.3 failure wearing a different hat.
 */

const TONE_TEXT = {
  positive: 'text-positive',
  caution: 'text-caution',
  critical: 'text-critical',
  /* No colour, per §3.4. The word carries it. */
  unmeasured: 'text-text-muted',
} as const

/**
 * The one-line explanation beside a state.
 *
 * When an open incident is what put the component here, the server sends the
 * incident's **reference** as the reason — and a reference repeated down four
 * rows is noise unless it is also the way to reach the incident. So a
 * reference-shaped reason becomes a link, and everything else ("no nodes
 * reporting", "region signal") stays text.
 *
 * The shape is matched rather than trusted: the reason is server-supplied and
 * ends up in a URL, so only a string that is exactly a well-formed reference is
 * allowed to become one. Anything else renders as the text it is.
 *
 * Outside `StateList` — FRONTEND_SPEC.md §5.1, `rerender-no-inline-components`.
 */
const REFERENCE = /^PC-INC-[A-Z0-9]{6}$/

function Reason({ reason }: { readonly reason: string }) {
  if (reason === '') return null
  if (!REFERENCE.test(reason)) {
    return <span className="text-text-muted"> · {reason}</span>
  }
  return (
    <>
      <span className="text-text-muted"> · </span>
      <a
        href={`/incidents/${reason}`}
        className="type-data rounded-xs text-text-muted underline-offset-2 hover:underline"
        translate="no"
      >
        {reason}
      </a>
    </>
  )
}

export function StateList({
  title,
  entries,
  empty,
  intro,
  footer,
  trailing,
  sectionRef,
}: {
  readonly title: string
  readonly entries: readonly StatusEntry[]
  /** What to say when the server sent no rows at all. Never "all operational". */
  readonly empty: string
  /** One line under the heading. The regions list uses it to say what its
   *  latency column is and — §10.1 — what it is not. */
  readonly intro?: ReactNode
  /** Below the plate. The regions list puts the Re-measure control here. */
  readonly footer?: ReactNode
  /**
   * Rendered at the inline end of a row, given its id.
   *
   * A slot rather than a `latency` prop, so this component stays what it is —
   * a plate of state rows — and knows nothing about probes. The components
   * list passes none and is unchanged; the regions list passes the measurement.
   */
  readonly trailing?: (id: string) => ReactNode
  /** Attached to the section, so a caller can observe its visibility (§10.3). */
  readonly sectionRef?: (node: HTMLElement | null) => void
}) {
  return (
    <section className="mt-8" ref={sectionRef}>
      <h2 className="type-label">{title}</h2>
      {intro === undefined ? null : (
        <p className="mt-1 text-xs text-text-muted">{intro}</p>
      )}
      <div className="mt-2 border border-border-default bg-surface-base">
        {entries.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-muted">{empty}</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-stretch gap-3">
                <StateMarker state={entry.state} />
                <div className="flex grow flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 pe-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium break-words text-text-primary">
                      {entry.name}
                    </p>
                    {entry.description === '' ? null : (
                      <p className="mt-0.5 text-xs text-text-muted">{entry.description}</p>
                    )}
                  </div>
                  <div className="flex items-baseline gap-x-4">
                    <p className={`text-sm ${TONE_TEXT[toneFor(entry.state)]}`}>
                      {labelFor(entry.state)}
                      <Reason reason={entry.reason} />
                    </p>
                    {trailing === undefined ? null : trailing(entry.id)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {footer === undefined ? null : footer}
    </section>
  )
}
